import * as React from 'react';
import {Icon, Modal, Upload} from 'antd';
import {UploadChangeParam} from 'antd/lib/upload';
import {RcCustomRequestOptions, UploadFile} from 'antd/lib/upload/interface';
import {ErrorResponse} from '../../api/dtos/dtos';
import locales from '../../locales';
import {ApplicationContextConsumer, bindLoadingHandler, LoadingOverlayHandler,} from '../../app/ApplicationContext';
import {showConfirm, showSaveErrorNotification, showSuccessNotification} from '../../utils/dialogs';
import './ImageUploadEditor.css';
import {blobToBase64} from "./image";

interface Props<T extends object> {
    onUpload: (file: Blob) => Promise<T>;
    onDelete: (file: UploadFile<T>) => Promise<void>;
    files: Array<UploadFile<T>>;
    disabled: boolean;
    required: boolean;
    deleteDisabled: boolean;
}

interface State<T extends object> {
    fileList: Array<UploadFile<T>>;
    previewVisible: boolean;
    previewImage?: string;
}

class ImageUploadEditor<T extends object> extends React.Component<Props<T>, State<T>> {
    public readonly loadingHandler?: LoadingOverlayHandler;

    constructor(props: Props<T>) {
        super(props);
        this.state = {
            fileList: props.files,
            previewVisible: false,
        };
    }

    public render() {
        const {disabled, deleteDisabled} = this.props;
        const {fileList, previewImage, previewVisible} = this.state;


        const uploadButton = (
            <div>
                <Icon type="plus"/>
                <div className="ant-upload-text">{locales.file_upload}</div>
            </div>
        );


        return (
            <>
                <ApplicationContextConsumer>
                    {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
                </ApplicationContextConsumer>

                {(fileList.length !== 0 || !disabled) ?
                    <Upload
                        disabled={disabled}
                        fileList={fileList}
                        multiple={true}
                        openFileDialogOnClick={true}
                        listType={'picture-card'}
                        customRequest={this.handleUpload}
                        onChange={this.handleUploadStateChange}
                        onPreview={this.handlePreview}
                        showUploadList={{showRemoveIcon: !deleteDisabled}}
                        accept={".png,.jpg,.jpeg"}
                    >
                        {!disabled && uploadButton}
                    </Upload>
                    :
                    <div style={{height: 100, margin: '8px 8px 8px 0'}}>
                        {locales.no_images_uploaded}
                    </div>
                }


                <Modal
                    className={"asset-preview-modal"}
                    visible={previewVisible}
                    footer={null}
                    onCancel={this.handleCancel}
                >
                    <img alt="example" style={{width: '100%'}} src={previewImage}/>
                </Modal>
            </>
        );
    }

    private handleUpload = async (request: RcCustomRequestOptions) => {
        const {file} = request;
        if (file instanceof Blob) {
            try {
                const response = await this.props.onUpload(file);
                request.onSuccess(response, file);
            } catch (e) {
                request.onError(e);
            }
        }
    }

    private handleCancel = () => this.setState({previewVisible: false});

    private handlePreview = async (file: UploadFile) => {
        if (!file.url && !file.preview) {
            file.preview = await blobToBase64(file.originFileObj as Blob);
        }

        this.setState({
            previewImage: file.url || file.preview,
            previewVisible: true,
        });
    };

    private handleUploadStateChange = (info: UploadChangeParam<UploadFile<T>>) => {
        const fileList = info.fileList.map((file) => {
            if (file.response && file.response.status !== 'error') {
                file.uid = file.response.id;
                file.name = file.response.name;
                file.url = file.response.url;
            } else if (file.response && file.response.status === 'error') {
                file.url = undefined;
                file.name = file.response.name;
            }
            return file;
        });

        if (info.file.status === 'removed') {
            showConfirm(locales.file_delete_confirm, () => this.removeFile(info.file, fileList))

        } else if (info.file.status === 'done') {
            showSuccessNotification(locales.file_upload_success);
            this.setFileList(fileList);
        } else if (info.file.status === 'error') {
            showSaveErrorNotification();
        } else {
            this.setFileList(fileList);
        }
    };

    private removeFile = (fileToDelete: UploadFile, remainingFileList: UploadFile[]) => {
        this.props.onDelete(fileToDelete)
            .then(() => {
                this.setFileList(remainingFileList);
                showSuccessNotification(locales.file_delete_success);
            })
            .catch((error: ErrorResponse) => {
                remainingFileList.push({
                    ...fileToDelete,
                    status: 'error',
                    response: error.message
                });
                this.setFileList(remainingFileList);
            });
    };

    private setFileList = (fileList: UploadFile[]) => {
        this.setState({fileList: fileList.map(item => ({...item, uid: `${item.uid}`}))});
    };
}

export default ImageUploadEditor;


// WEBPACK FOOTER //
// ./src/common/input/ImageUploadEditor.tsx